// apps/web/src/features/dashai/lib/spikeDropClient.ts
//
// Shared client helper for the Spike-Drop V2 Firebase function. Exposes:
//   - postWithAuthRetry (re-exported from onePSfaClient)
//   - getSpikeDropUrl()
//   - fetchSpikeDropRefreshes(clientId, country) — JSON mode
//   - streamSpikeDrop(body, signal?) — SSE mode, returns the raw Response
//     so callers can iterate via getReader().read()

export { postWithAuthRetry } from './onePSfaClient';

import { postWithAuthRetry } from './onePSfaClient';

export interface ValidatedRefresh {
  refresh: string;
  rowCount: number;
  isComplete: boolean;
}

export interface SpikeDropRequest {
  clientId: string;
  country: string;
  startRefresh: string;
  endRefresh: string;
  performanceMetrics: string[];
  filters?: Record<string, unknown>;
  insightContext?: unknown;
  /** Saved filter view inlined by the picker; scopes the whole analysis universe. */
  filter_view?: unknown;
}

export function getSpikeDropUrl(): string | undefined {
  const explicit = import.meta.env.VITE_SPIKE_DROP_URL as string | undefined;
  if (explicit) return explicit;
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (base) return `${base.replace(/\/+$/, '')}/dashaiSpikeDrop`;
  return undefined;
}

export async function fetchSpikeDropRefreshes(
  clientId: string,
  country: string,
): Promise<ValidatedRefresh[]> {
  const url = getSpikeDropUrl();
  if (!url) throw new Error('Spike-Drop function URL not configured');
  const res = await postWithAuthRetry(url, { mode: 'refreshes', clientId, country });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `fetchSpikeDropRefreshes failed (${res.status})`);
  }
  return (await res.json()) as ValidatedRefresh[];
}

/** Longest failure reason carried into a persisted chat message. */
const MAX_REASON_CHARS = 300;

/** The payloads `dashaiSpikeDrop` emits on its SSE stream. */
export type SpikeDropFrame =
  | { type: 'phase'; phase: number; title: string; detail: string; status: 'pending' | 'active' | 'complete' }
  | { type: 'deck'; data: unknown }
  | { type: 'error'; message?: string }
  | { type: 'done' };

/**
 * Split an SSE buffer into whole `data:` frames, returning the parsed payloads
 * and whatever tail is not yet a complete line.
 *
 * `final` marks the read that saw the stream close. The tail is then a frame
 * the server never terminated, which means the response was cut mid-write. It
 * is reported as `truncated` rather than parsed, because dropping it is how a
 * cut stream turns into a silent no-op.
 */
export function readSseFrames<Frame = SpikeDropFrame>(
  buffer: string,
  final: boolean,
): { events: Frame[]; rest: string; truncated: boolean } {
  const lines = buffer.split('\n');
  const rest = final ? '' : (lines.pop() ?? '');
  const tail = final ? lines[lines.length - 1] : '';
  const truncated = final && tail.startsWith('data: ') && !isParsable(tail.slice(6));

  const events: Frame[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data: ')) continue;
    try {
      events.push(JSON.parse(trimmed.slice(6)) as Frame);
    } catch {
      // A frame that will not parse carries nothing actionable. When it is the
      // unterminated tail, `truncated` is what the caller acts on.
    }
  }
  return { events, rest, truncated };
}

function isParsable(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Unwrap the function's `{"error":"…"}` body so callers surface the reason
 * rather than the transport.
 *
 * Anything that is not that envelope is summarised by status alone. The caller
 * persists this string into the chat, and a body from an intermediary — a
 * gateway's HTML 502, say — can name internal hosts and revision ids that do
 * not belong in a tenant's stored history. The raw text stays in the console.
 */
function describeFailure(text: string, status: number): string {
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === 'string' && parsed.error) {
      return parsed.error.slice(0, MAX_REASON_CHARS);
    }
  } catch {
    // Not the error envelope; fall through to the status-only summary.
  }
  if (text) console.error('[spike-drop] unrecognised error body:', text.slice(0, 2000));
  return `The analysis service returned ${status}.`;
}

export async function streamSpikeDrop(
  body: SpikeDropRequest,
  signal?: AbortSignal,
): Promise<Response> {
  const url = getSpikeDropUrl();
  if (!url) throw new Error('Spike-Drop function URL not configured');
  // Both period bounds are structural: the analysis resolves a metric delta
  // between them, and the function rejects a request missing either. Caught
  // here so the caller gets a named reason instead of a round-trip and a 400.
  for (const bound of ['startRefresh', 'endRefresh'] as const) {
    if (!body[bound]) throw new Error(`${bound} is required`);
  }
  const res = await postWithAuthRetry(url, body, signal);
  if (!res.ok) {
    throw new Error(describeFailure(await res.text(), res.status));
  }
  return res;
}
